import { DEFAULT_CATALOG_SORT } from '@game-keys/shared'
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api, unwrap } from './api'

export type CatalogResponse = Awaited<ReturnType<typeof fetchCatalog>>
export type CatalogGame = CatalogResponse['games'][number]
export type Store = Awaited<ReturnType<typeof fetchStores>>[number]
export type GameDetail = Awaited<ReturnType<typeof fetchGame>>

/**
 * Catalog filters as they live in the URL.
 *
 * Multi-value facets are comma-joined strings rather than repeated params, so
 * a filtered view is a short, readable link someone can paste into a chat.
 */
export type CatalogParams = {
  q?: string
  stores?: string
  genres?: string
  platforms?: string
  /** What a key unlocks: base, bundle, dlc, other. */
  unlock?: string
  /** Release year bounds, inclusive. */
  from?: number
  to?: number
  /** Minimum review score, as a whole percentage. */
  minScore?: number
  filter?: 'all' | 'expiring'
  sort?: 'recent' | 'released' | 'score' | 'title' | 'copies'
  page?: number
}

async function fetchCatalog(params: CatalogParams, perPage = 60) {
  return unwrap(
    await api.games.$get({
      query: {
        ...(params.q ? { q: params.q } : {}),
        ...(params.stores ? { stores: params.stores } : {}),
        ...(params.genres ? { genres: params.genres } : {}),
        ...(params.platforms ? { platforms: params.platforms } : {}),
        ...(params.unlock ? { unlock: params.unlock } : {}),
        ...(params.from ? { from: String(params.from) } : {}),
        ...(params.to ? { to: String(params.to) } : {}),
        ...(params.minScore ? { minScore: String(params.minScore) } : {}),
        filter: params.filter ?? 'all',
        sort: params.sort ?? DEFAULT_CATALOG_SORT,
        page: String(params.page ?? 1),
        perPage: String(perPage),
      },
    }),
  )
}

async function fetchStores() {
  return unwrap(await api.stores.$get())
}

async function fetchGame(slug: string) {
  return unwrap(await api.games[':slug'].$get({ param: { slug } }))
}

export function useCatalog(params: CatalogParams) {
  return useQuery({
    queryKey: ['catalog', params],
    queryFn: () => fetchCatalog(params),
    // Keeps the current grid on screen while a filter change loads, so the
    // page does not collapse to a skeleton on every checkbox click.
    placeholderData: keepPreviousData,
  })
}

/**
 * The catalog, a page at a time.
 *
 * The grid previously fetched a single page while displaying the true total,
 * so a library larger than one page silently hid the remainder — it claimed
 * 174 games and showed 120, with no way to reach the rest.
 */
export function useCatalogPages(params: CatalogParams) {
  return useInfiniteQuery({
    queryKey: ['catalog', 'pages', params],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchCatalog({ ...params, page: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.games.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
    // Keeps the current grid on screen while a filter change loads.
    placeholderData: keepPreviousData,
  })
}

export function useFacets() {
  return useQuery({
    queryKey: ['facets'],
    queryFn: async () => unwrap(await api.facets.$get()),
    staleTime: 5 * 60_000,
  })
}

export function useGame(slug: string) {
  return useQuery({
    queryKey: ['game', slug],
    queryFn: () => fetchGame(slug),
  })
}

export function useStores() {
  return useQuery({
    queryKey: ['stores'],
    queryFn: fetchStores,
    staleTime: 30 * 60_000,
  })
}

export function useConfig() {
  return useQuery({
    queryKey: ['config'],
    queryFn: async () => unwrap(await api.config.$get()),
    staleTime: 30 * 60_000,
  })
}

/** Lookup of store slug -> display data, for badges and dots. */
export function useStoreMap(): Map<string, Store> {
  const { data } = useStores()
  return new Map((data ?? []).map((store) => [store.slug, store]))
}

/** `"gog,epic"` <-> `["gog", "epic"]`, with empty meaning "no filter". */
export function parseList(value: string | undefined): string[] {
  return value ? value.split(',').filter(Boolean) : []
}

export function toggleInList(value: string | undefined, item: string): string | undefined {
  const list = parseList(value)
  const next = list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item]

  return next.length > 0 ? next.join(',') : undefined
}
